/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 로그인 로직이 포함된 로그인 탭
 * 버전 : 1.0
 * 파일명 : LoginTap.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-07-10
 * ================================================================ 
 * 수정 : 2025-07-14, 강동형 : DeferredPrompt 전역변수 변경으로 인한 로직 수정
 * 수정 : 2026-02-04, 강동형 : 갱신시점 변경
 * ================================================================ */

import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import logo from 'src/assets/images/Army_Link.svg' // 로고 이미지 경로를 적절히 설정하세요.
import { postToken } from 'src/api/Auth'
import { useStorageStore } from 'src/stores/StorageStore';
import InputBox from 'src/components/common/InputBox';
import { jwtDecode } from 'jwt-decode'
import { refetchUser } from 'src/utils/RefetchUser';
import icon_download  from 'src/assets/images/Icon_Download.svg'
import { toast } from 'react-toastify';
import { TempUserStore } from 'src/stores/UserInfoStore';
import { useNavbarStore, usePwaStore } from 'src/stores/UIStore';
import { useModalStore } from 'src/stores/ModalStore';
import { ModalContentBlock, ModalFooterBlock, ModalTitleBlock } from './LoginModalField';
import { Icon_Inform_Teal } from 'src/components/common/CommonIcons';
import { NextButton } from 'src/components/common/CommonStyled';
import { LoginPageTabType } from './LoginPage';

const LoginPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(var(--vh, 1vh) * 100);
    background-color: white; // 배경을 흰색으로 고정합니다.
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    width: calc(100% - 40px); // 가로로 길게 구성합니다.
    border-radius: 8px;
    position: relative; // 자식 요소의 절대 위치를 기준으로 합니다.
`;

const SignupButton = styled(Link)`
    display: flex;
    justify-content: center;
    align-items: center;

    padding: 13.5px;
    font-size: 16px;
    border-radius: 6px;
    background-color: white; // 배경을 흰색으로 설정합니다.
    cursor: pointer;
    /* margin-bottom: 0.4rem; */
    color: rgba(10,178,228,1); // 글씨 색상을 파랑색으로 설정합니다.
    border: 1px solid #229596;
    text-decoration: none; // 밑줄을 제거합니다.
    box-sizing: border-box;
    font-family: 'Pretendard';
    max-height: 46px;
`;

const LinkContainer = styled.div`
    align-items: center;
    display: flex;
    gap: 5px;
    white-space: nowrap;
    margin-left: auto;
    font-size: 14px;
    a {
        color: black; // 링크 텍스트 색상을 검은색으로 설정합니다.
        text-decoration: none; // 링크 밑줄을 제거합니다.
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* margin-bottom: 2rem; */

    input {
        margin-right: 0.3rem;
    }
    label {
        margin-left: 0.3rem; // 체크박스와 레이블간의 간격을 설정합니다.
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 86px;

    img {
        width: 256px;
        height: auto;
    }
`;

const InputContainer = styled.div`
    width: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 10px;
`

const InstallButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    position: absolute;

    padding:10px 16.5px;
    max-height: 44px;
    background-color: #E9F4F4;
    color: #229596;
    border: 1px solid #229596;
    border-radius: 5px;
    bottom: 8.5vh;
    max-width: 90%;
`

const InvalidSpan = styled.span<{ visible: boolean }>`
    font-size: 14px;
    color: red;
    display: ${({ visible }) => (visible ? 'inline' : 'none')};
    position:absolute;
`

interface Props{
    setTab: React.Dispatch<React.SetStateAction<LoginPageTabType>>;
}

const LoginTap = ({setTab}:Props) => {
    const storage = useStorageStore();
    const username = useRef('')
    const password = useRef('');
    const [autoLogin, setAutoLogin] = useState(() => {return storage.getItem('autoLogin') === 'true';});
    const [isVisible, setIsVisible] = useState(false)
    const navigate = useNavigate();
    const setField = TempUserStore((state)=>state.setField)
    const setNavField= useNavbarStore((state)=>state.setField)
    const modalSetField = useModalStore((state)=>state.setField)
    const whichDevice = usePwaStore((state)=>state.whichDevice)
    const deferredPrompt = usePwaStore((state)=>state.deferredPrompt)
    const clearDeferredPrompt = usePwaStore((state)=>state.clearDeferredPrompt)
    const isInsatalled = usePwaStore((state)=>state.isInsatalled)

    useEffect(()=>{
        setNavField('isvisible',false)
        return()=>{
            setNavField('isvisible',true)
        }
    },[])

    const handleAutoLogin = (e:boolean)=>{
        storage.setItem('autoLogin', e.toString())
        setAutoLogin(e)
    }

    const openBlockmodal = ()=>{
        modalSetField('type','BOTTOM')
        modalSetField('title',ModalTitleBlock)
        modalSetField('content',ModalContentBlock)
        modalSetField('footer',<ModalFooterBlock />)
        modalSetField('open',true)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const response = await postToken(username.current, password.current);
        
            if (response.access_token) {
                // 1) 토큰 저장이 끝날 때까지 기다림
                await storage.setItem("token", response);
                setNavField('isSideBarOpen',false)
            } else {
                console.error("로그인 실패:", response.message);
                setIsVisible(true)
                if(response.status === 424){
                openBlockmodal()
                }
                if(response.status === 423){
                setField('userName',username.current)
                navigate('/signup-deny')
                }
                return
            }
            await refetchUser()
            // 2) 그 다음에 페이지 이동
            navigate('/home');
        } catch (error) {
            console.error("로그인 요청 오류:", error);
        }
    };

    useEffect(() => {
        const token = storage.getItem("token");
        const isAutoLogin = storage.getItem("autoLogin") === 'true';

        if (!token || !isAutoLogin) return;

        try {
            const decoded = jwtDecode<{ exp: number }>(token.access_token);
            const now = Math.floor(Date.now() / 1000);

            if (decoded.exp > now) {
                navigate('/home', { replace: true });; // ✅ 유효한 경우에만 이동
            } else {
                console.warn("자동 로그인 실패: 토큰 만료됨");
                toast.error('자동 로그인이 만료되었습니다.')
                storage.removeItem("token");
            }
        } catch (e) {
            console.error("자동 로그인 실패: 토큰 파싱 에러");
            storage.removeItem("token");
        }
    }, []);

    const createShortCut = async ()=>{
        if (!deferredPrompt) {
            toast.error('바로가기가 이미 있습니다.')
            return;
        }

        deferredPrompt.prompt(); // 설치 다이얼로그 표시

        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
        // console.log('사용자가 설치함');
        } else {
        // console.log('사용자가 설치 취소함');
        }

        clearDeferredPrompt(); // 재사용 불가
    }
    
    return (
        <LoginPageContainer>      
            <Form onSubmit={handleSubmit}>
                <HeaderContainer>
                <img src={logo} alt="로그인 로고" />
                </HeaderContainer>
                <InputContainer>
                    <InputBox
                    type="text"
                    placeholder="아이디" // placeholder를 "아이디"로 설정합니다.
                    value={username}
                    />
                    <InputBox
                    type="password"
                    placeholder="비밀번호" // placeholder를 "비밀번호"로 설정합니다.
                    value={password}
                    />
                </InputContainer>
                <ToggleContainer>
                <input
                    type="checkbox"
                    checked={autoLogin}
                    onChange={(e) => handleAutoLogin(e.target.checked)}
                />
                <label style={{whiteSpace:"nowrap", fontSize:'14px'}}>자동 로그인</label>
                <LinkContainer>
                    <Link to="/find-username">아이디 찾기</Link>
                    <span>|</span>
                    <Link to="/find-password">비밀번호 찾기</Link>
                </LinkContainer>
                </ToggleContainer>
                <div style={{position:'relative'}}>
                <InvalidSpan visible={isVisible}>* 아이디 또는 비밀번호 오류입니다.</InvalidSpan>
                </div>
                <NextButton type="submit" style={{marginTop:'41.5px', marginBottom:'10px', height:'46px'}}>로그인</NextButton>
                <SignupButton as={Link} to="/signup">회원가입</SignupButton>
            </Form>
            {whichDevice === 'ios' ?  
                (isInsatalled === false && 
                    <InstallButton onClick={()=>setTab('InstallGuide')} style={{padding:'10px 46px'}}>
                        <Icon_Inform_Teal/>
                        홈 화면 바로가기 설치 가이드
                    </InstallButton>)
                :
                (deferredPrompt && 
                    <InstallButton onClick={createShortCut}>
                        <img src={icon_download} alt="" />
                        홈 화면에 설치하기
                    </InstallButton>
                )
            }
        </LoginPageContainer>
    )
}

export default LoginTap